import * as diffLib from 'diff';

import { stripHtml } from '@/lib/utils/stripHtml';

// Format date for display
export const formatDate = (dateStr: string) => {
  if (!dateStr || dateStr === 'Vyhlásené znenie' || dateStr === '')
    return dateStr;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sk-SK');
  } catch {
    return dateStr;
  }
};

// Clean and prepare HTML for rendering
export const prepareHtml = (html: string) => {
  if (!html) return '';
  // Remove unnecessary line breaks at the beginning
  return html.replace(/^\s*<br\/>\s*<br\/>\s*/, '');
};

// Helper function to escape special characters in regex
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Calculate stats for the comparison
export const calculateStats = (oldText: string, newText: string) => {
  console.log('📊 calculateStats called with:');
  console.log(
    '  - oldText (should be selected version):',
    `${oldText?.substring(0, 50)}...`
  );
  console.log(
    '  - newText (should be current version):',
    `${newText?.substring(0, 50)}...`
  );
  console.log('  - oldText length:', oldText?.length);
  console.log('  - newText length:', newText?.length);

  // Handle empty text cases correctly
  if (!oldText && !newText) {
    // Both empty = identical
    console.log('  - Case: Both empty, returning identical');
    return { added: 0, removed: 0, unchanged: 0 };
  }

  if (!oldText) {
    // Left empty, right has content = all content is "added"
    const strippedNew = stripHtml(newText);
    console.log('  - Case: oldText empty, strippedNew:', strippedNew);
    const wordCount = strippedNew.split(/\s+/).filter(Boolean).length;
    console.log('  - wordCount added:', wordCount);
    return { added: wordCount, removed: 0, unchanged: 0 };
  }

  if (!newText) {
    // Right empty, left has content = all content is "removed"
    const strippedOld = stripHtml(oldText);
    console.log('  - Case: newText empty, strippedOld:', strippedOld);
    const wordCount = strippedOld.split(/\s+/).filter(Boolean).length;
    console.log('  - wordCount removed:', wordCount);
    return { added: 0, removed: wordCount, unchanged: 0 };
  }

  // Both have content - do normal diff calculation
  console.log('  - Case: Both have content, doing diff');
  const strippedOld = stripHtml(oldText);
  const strippedNew = stripHtml(newText);
  console.log('  - strippedOld:', strippedOld);
  console.log('  - strippedNew:', strippedNew);

  const diff = diffLib.diffWords(strippedOld, strippedNew);
  console.log('  - diff result:', diff);

  const result = diff.reduce(
    (acc, part) => {
      const count = part.value.split(/\s+/).filter(Boolean).length;
      if (part.added) acc.added += count;
      else if (part.removed) acc.removed += count;
      else acc.unchanged += count;
      return acc;
    },
    { added: 0, removed: 0, unchanged: 0 }
  );

  console.log('  - Final result:', result);
  return result;
};

// Add markers to the HTML for word-level differences
export const addDiffMarkers = (
  oldHtml: string,
  newHtml: string,
  isFirstVersion: boolean
): string => {
  try {
    console.log(
      `🎨 addDiffMarkers for ${isFirstVersion ? 'LEFT' : 'RIGHT'} panel`
    );

    // Get plain text for comparison
    const oldText = stripHtml(oldHtml);
    const newText = stripHtml(newHtml);

    console.log(
      '  - oldText length:',
      oldText?.length,
      'newText length:',
      newText?.length
    );

    // Handle empty text cases
    if (!oldText && !newText) {
      return ''; // Both empty
    }

    if (!oldText) {
      // Left empty, right has content
      console.log(
        '  - Case: oldText empty, showing content in',
        isFirstVersion ? 'LEFT' : 'RIGHT'
      );
      return isFirstVersion
        ? ''
        : `<span class="new-version-highlight">${newHtml}</span>`;
    }

    if (!newText) {
      // Right empty, left has content
      console.log(
        '  - Case: newText empty, showing content in',
        isFirstVersion ? 'LEFT' : 'RIGHT'
      );
      return isFirstVersion
        ? `<span class="old-version-highlight">${oldHtml}</span>`
        : '';
    }

    // Return original if no differences or in case of errors
    if (oldText === newText) {
      console.log('  - Case: texts identical');
      return isFirstVersion ? oldHtml : newHtml;
    }

    console.log('  - Case: applying diff highlighting');

    // Create a copy of the HTML to work with
    let resultHtml = isFirstVersion ? oldHtml : newHtml;

    // Perform diff at word level
    const diff = diffLib.diffWords(oldText, newText);

    // For first version (left side), we highlight text that's only in the old version
    if (isFirstVersion) {
      // Find words that are only in the old version (removed in the new version)
      const removedParts = diff
        .filter((part) => part.removed)
        .map((part) => part.value);

      // Highlight each removed part in the original HTML
      for (const removedText of removedParts) {
        if (removedText.trim() === '') continue; // Skip empty parts

        // Use word boundaries to match whole words/phrases
        const regex = new RegExp(`\\b${escapeRegExp(removedText)}\\b`, 'g');
        resultHtml = resultHtml.replace(
          regex,
          `<span class="old-version-highlight">${removedText}</span>`
        );
      }
    }
    // For second version (right side), we highlight text that's only in the new version
    else {
      // Find words that are only in the new version (added)
      const addedParts = diff
        .filter((part) => part.added)
        .map((part) => part.value);

      // Highlight each added part in the new HTML
      for (const addedText of addedParts) {
        if (addedText.trim() === '') continue; // Skip empty parts

        // Use word boundaries to match whole words/phrases
        const regex = new RegExp(`\\b${escapeRegExp(addedText)}\\b`, 'g');
        resultHtml = resultHtml.replace(
          regex,
          `<span class="new-version-highlight">${addedText}</span>`
        );
      }
    }

    return resultHtml;
  } catch (error) {
    console.error('Error in addDiffMarkers:', error);
    return isFirstVersion ? oldHtml : newHtml; // Return original on error
  }
};
